import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ModalCard, useCreateModal } from 'partial/components/Modal';
import FormTextArea from 'partial/components/form/FormTextArea';
import Button from 'partial/components/Button';
import Form from 'partial/components/form';
import FormInput from 'partial/components/form/FormInput';
import classNames from 'classnames';

import { useAddDomain, useEditDomain } from '../hooks';

export const useOpenDomainSetup = () => {
  const createModal = useCreateModal();

  const openDomainSetup = useCallback(
    (data) => {
      createModal({
        content: (close) => <DomainSetup data={data} onClose={close} />,
      });
    },
    [createModal],
  );

  return openDomainSetup;
};

const validationSchema = yup.object().shape({
  domain: yup.string().min(1, 'Too short').required('Required'),
  remarks: yup.string().min(1, 'Too short').max(200, 'Too long'),
  with_subdomain: yup.bool(),
});

const titleConfig = {
  add: 'ADD',
  edit: 'UPDATE',
};

const hookConfig = {
  add: useAddDomain,
  edit: useEditDomain,
};

function DomainSetup({ data, onClose }) {
  const { type, organization, domain = {} } = data;
  const hook = hookConfig[type];
  const [loading, action] = hook(onClose);
  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: { domain: '', remarks: '', with_subdomain: 0 },
    validationSchema,
    onSubmit: (payload) => {
      action({
        uuid: organization?.uuid,
        ...(type === 'edit' && { domainUuid: domain?.uuid }),
        ...payload,
      });
    },
    validateOnChange: false,
  });

  useEffect(() => {
    if (type === 'add') return;
    setFieldValue('domain', domain?.domain);
    setFieldValue('remarks', domain?.remarks);
    setFieldValue('with_subdomain', domain?.with_subdomain);
  }, [type, domain, setFieldValue]);

  return (
    <ModalCard
      size="sm max-h-[90vh] overflow-y-auto"
      title={`${titleConfig[type]} DOMAIN`}
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit} className="px-5 pb-5 w-full">
        <div className="space-y-6">
          <div className="space-y-4">
            <FormInput
              name="domain"
              onSetFieldValue={setFieldValue}
              value={values.domain}
              error={errors.domain}
              label="Domain"
              required
            />
            <FormTextArea
              name="remarks"
              onSetFieldValue={setFieldValue}
              value={values.remarks}
              error={errors.remarks}
              label="Remarks"
            />
            <div className="text-sm flex items-center space-x-2 py-2">
              <div>
                <input
                  id="with_subdomain"
                  type="checkbox"
                  className="form-checkbox rounded h-5 w-5 text-primary !cursor-pointer focus:outline-none focus:ring-0 border-primary"
                  onChange={({ target }) =>
                    setFieldValue('with_subdomain', target.checked)
                  }
                  checked={values.with_subdomain}
                />
              </div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="with_subdomain"
                className="noselect !cursor-pointer mt-px"
              >
                <span
                  className={classNames(
                    'transition',
                    values?.with_subdomain ? 'text-primary' : '',
                  )}
                >
                  With subdomain
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                onClick={onClose}
                outline
                className="uppercase"
                disabled={loading}
              >
                cancel
              </Button>
              <Button
                type="submit"
                primary
                className="uppercase"
                disabled={loading}
              >
                {type === 'add' ? 'Add' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </ModalCard>
  );
}

DomainSetup.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DomainSetup;
